import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import atributoService from '../services/atributoService';
import plantillasContratoService from '../services/plantillasContratoService';
import PlantillaContratoModal from '../components/PlantillaContratoModal';
import ImportarPlantillasContratoModal from '../components/ImportarPlantillasContratoModal';
import Toast from '../components/Toast';

const Plantillas = () => {
  const { selectedCompany } = useAuth();
  const [plantillas, setPlantillas] = useState([]);
  const [tiposContrato, setTiposContrato] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modalImportarAbierto, setModalImportarAbierto] = useState(false);
  const [plantillaEditando, setPlantillaEditando] = useState(null);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('info');

  const mostrarMensaje = (tipo, texto) => {
    setToastType(tipo);
    setToastMessage(texto);
  };

  const cargarDatos = async () => {
    if (!selectedCompany?.id) {
      setPlantillas([]);
      setTiposContrato([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const [plantillasResponse, atributosResponse] = await Promise.all([
        plantillasContratoService.obtenerPlantillas(selectedCompany.id),
        atributoService.obtenerConfiguracionCompleta(selectedCompany.id),
      ]);

      if (plantillasResponse.success) {
        setPlantillas(plantillasResponse.data);
      } else {
        setPlantillas([]);
        mostrarMensaje('error', plantillasResponse.error || 'Error al cargar plantillas');
      }

      const subAtributos = atributosResponse.success
        ? atributosResponse.data?.sub_atributos?.['Tipos de Contratación'] || []
        : [];

      setTiposContrato(
        subAtributos
          .map((item) => item.nombre || item.valor || item.descripcion)
          .filter(Boolean)
      );
    } catch (error) {
      mostrarMensaje('error', 'Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, [selectedCompany?.id]);

  const plantillasFiltradas = useMemo(() => {
    if (!filtroTipo) return plantillas;
    return plantillas.filter((item) => item.tipo === filtroTipo);
  }, [plantillas, filtroTipo]);

  const plantillasOrdenadas = useMemo(() => {
    return [...plantillasFiltradas].sort((a, b) =>
      String(a.titulo || '').localeCompare(String(b.titulo || ''), 'es', { sensitivity: 'base' })
    );
  }, [plantillasFiltradas]);

  const abrirNuevaPlantilla = () => {
    setPlantillaEditando(null);
    setModalAbierto(true);
  };

  const abrirEditarPlantilla = (plantilla) => {
    setPlantillaEditando(plantilla);
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setPlantillaEditando(null);
  };

  const eliminarPlantilla = async (plantilla) => {
    if (!window.confirm(`¿Eliminar la plantilla "${plantilla.titulo}"?`)) return;

    const response = await plantillasContratoService.eliminarPlantilla(plantilla.id);
    if (response.success) {
      mostrarMensaje('success', response.message);
      cargarDatos();
    } else {
      mostrarMensaje('error', response.error || 'No se pudo eliminar la plantilla');
    }
  };

  if (!selectedCompany) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center text-gray-600">
        Selecciona una empresa para gestionar plantillas de contrato.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Plantillas de contratos</h2>
            <p className="text-gray-600 mt-1">
              Lista de plantillas ordenada por título. El tipo de contrato se muestra en cada plantilla y puedes filtrarlo arriba.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => setModalImportarAbierto(true)}
              className="px-4 py-2 border border-indigo-200 text-indigo-700 rounded-lg hover:bg-indigo-50"
            >
              Importar de otra empresa
            </button>
            <button
              type="button"
              onClick={abrirNuevaPlantilla}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              + Nueva plantilla
            </button>
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Todos los tipos</option>
            {tiposContrato.map((tipo) => (
              <option key={tipo} value={tipo}>{tipo}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center text-gray-500">
          Cargando plantillas...
        </div>
      ) : plantillasFiltradas.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <p className="text-gray-600 mb-4">No hay plantillas registradas todavía.</p>
          <button
            type="button"
            onClick={abrirNuevaPlantilla}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Crear primera plantilla
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {plantillasOrdenadas.length} plantilla(s)
            </p>
          </div>
          <div className="divide-y divide-gray-200">
            {plantillasOrdenadas.map((plantilla) => (
              <div key={plantilla.id} className="px-6 py-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <h4 className="text-base font-semibold text-gray-900">{plantilla.titulo}</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    {plantilla.tipo ? (
                      <span className="inline-flex items-center mr-2 px-2 py-0.5 text-xs font-medium bg-indigo-50 text-indigo-700 rounded">
                        {plantilla.tipo}
                      </span>
                    ) : null}
                    {Array.isArray(plantilla.articulos) ? plantilla.articulos.length : 0} artículo(s)
                    {Array.isArray(plantilla.variables) && plantilla.variables.length > 0 && (
                      <> · {plantilla.variables.length} variable(s)</>
                    )}
                  </p>
                  {Array.isArray(plantilla.variables) && plantilla.variables.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {plantilla.variables.slice(0, 6).map((variable) => (
                        <span
                          key={variable.key}
                          className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded"
                        >
                          {variable.label || variable.key}
                        </span>
                      ))}
                      {plantilla.variables.length > 6 && (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">
                          +{plantilla.variables.length - 6} más
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => abrirEditarPlantilla(plantilla)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => eliminarPlantilla(plantilla)}
                    className="px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ImportarPlantillasContratoModal
        isOpen={modalImportarAbierto}
        onClose={() => setModalImportarAbierto(false)}
        empresaDestinoId={selectedCompany.id}
        onSuccess={(mensaje) => {
          mostrarMensaje('success', mensaje || 'Plantillas importadas');
          cargarDatos();
        }}
      />

      <PlantillaContratoModal
        isOpen={modalAbierto}
        onClose={cerrarModal}
        empresaId={selectedCompany.id}
        empresa={selectedCompany}
        tiposContrato={tiposContrato}
        plantilla={plantillaEditando}
        onSuccess={(mensaje) => {
          mostrarMensaje('success', mensaje || 'Plantilla guardada');
          cargarDatos();
        }}
      />

      <Toast
        message={toastMessage}
        type={toastType}
        onClose={() => setToastMessage('')}
      />
    </div>
  );
};

export default Plantillas;
